<template>
  <div :data-testid="dataTestid" class="h-32 w-full">
    <VChart :option="option" :theme="OMR_ECHARTS_THEME" :data-testid="`${dataTestid}-chart`" autoresize class="h-full w-full" />
  </div>
</template>

<script lang="ts">
export interface InsightsStackedBarSegment {
  label: string
  value: number
  color: string
}
</script>

<script setup lang="ts">
import type { BarSeriesOption } from 'echarts/charts'
import { format } from 'echarts/core'
import { computed } from 'vue'

import { OMR_ECHARTS_THEME } from '@/composables/useOmrEchartsTheme'
import { VChart, type OmrEChartsOption } from '@/config/echarts'
import { makeRatioPercent } from '@/helpers/chartFormat'

const props = withDefaults(
  defineProps<{
    segments: InsightsStackedBarSegment[]
    locale: string
    dataTestid?: string
  }>(),
  { dataTestid: 'insights-stacked-bar' },
)

const option = computed<OmrEChartsOption>(() => {
  const percent = makeRatioPercent(props.locale)
  const total = props.segments.reduce((sum, segment) => sum + segment.value, 0)
  const share = (value: number): number => (total > 0 ? value / total : 0)

  return {
    tooltip: {
      trigger: 'item',
      formatter: (params) => {
        const point = Array.isArray(params) ? params[0] : params
        return `${format.encodeHTML(String(point.seriesName))}: ${percent(Number(point.value))}`
      },
    },
    grid: { top: 8, left: 0, right: 0, bottom: 48, containLabel: false },
    legend: {
      bottom: 0,
      formatter: (name) => {
        const segment = props.segments.find((item) => item.label === name)
        return segment ? `${name}  ${percent(share(segment.value))}` : name
      },
    },
    xAxis: { type: 'value', min: 0, max: 1, show: false },
    yAxis: { type: 'category', data: [''], show: false },
    series: props.segments.map((segment, index): BarSeriesOption => {
      const isFirst = index === 0
      const isLast = index === props.segments.length - 1

      return {
        type: 'bar',
        name: segment.label,
        stack: 'total',
        barWidth: 34,
        data: [share(segment.value)],
        itemStyle: {
          color: segment.color,
          borderRadius: isFirst ? [4, 0, 0, 4] : isLast ? [0, 4, 4, 0] : 0,
        },
        label: { show: false },
      }
    }),
  }
})
</script>
